import {
    AddressCategory,
    LaneGroupTemperatureRange,
    LaneStatus,
    LeaseType,
    LocationType,
    ProductType,
} from 'app/enums/label.enums'
import {
    RateCardsData,
} from 'app/requests/RateCard/RateCard.types'
import {
    DTOCardFormField, FieldInCard, NestedField,
} from 'app/types/form.types'
import {
    Country,
} from 'app/Apps/ContactManagement/Countries/Countries.types'
import {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink'

import {
    OpportunityFilters,
} from 'app/shared-components/OpportunitiesSelector/OpportunititesSelector.types'
import {
    AccountLane, AccountLaneProductTypes,
} from '../AccountLanes.types'
import getHandoverAlternateFields from './sections/handoverAlternate.fields'
import getCollectionAlternateFields from './sections/collectionAlternate.fields'
import getHandoverFields from './sections/handover.fields'
import getCollectionFields from './sections/collection.fields'

export type AccountLaneFormFields = AccountLane & AccountLaneProductTypes & {
    originPositioningFtl?: boolean,
    originPositioningLtl?: boolean,
    originTruckCapacity?: number,
    destinationCollectionLtl?: boolean,
}

const getFields = ({
    getAllowedValues,
    showFTL,
    showLTL,
    isDeliveryRoad,
    isCollection,
    handoverPointCountry,
    collectionDropoffPointCountry,
    showProductTypes,
    showAwardedProductTypes,
    exists,
    quoteInLink,
    quoteInNumber,
    disabled,
    laneLeadPermission,
    requiredBillingCompany,
    tempRangeDisabled,
    accountDisabled,
    opportunityFilters,
    showHandoverCollectionSection,
    logisticDetails,
    isAlternativeFlow,
}: {
    getAllowedValues: (val: Record<string, string>) => Record<string, string>,
    showFTL: boolean,
    showLTL: boolean,
    isDeliveryRoad: boolean,
    isCollection: boolean,
    handoverPointCountry?: Country | { id: number },
    collectionDropoffPointCountry?: Country | { id: number },
    showProductTypes?: boolean,
    showAwardedProductTypes?: boolean,
    exists: boolean,
    quoteInLink?: string,
    quoteInNumber?: string,
    disabled?: boolean,
    isAccountAdmin?: boolean,
    laneLeadPermission?: boolean,
    requiredBillingCompany?: boolean,
    tempRangeDisabled?: boolean,
    accountDisabled?: boolean,
    opportunityFilters: OpportunityFilters,
    showHandoverCollectionSection: boolean,
    logisticDetails?: RateCardsData,
    isAlternativeFlow?: boolean
}): DTOCardFormField<keyof AccountLaneFormFields>[] => {
    const generalFields: FieldInCard<keyof AccountLaneFormFields>[] = [
        {
            id: 'column1',
            fields: [
                {
                    name: 'laneNumber',
                    componentName: 'InputSingleline',
                    labelKey: 'AccountLane.laneNumber',
                    disabled: true,
                },
                {
                    name: 'account',
                    componentName: 'AccountSelector',
                    labelKey: 'AccountLane.accountCompanyName',
                    required: true,
                    disabled: disabled || accountDisabled,
                },
                ...exists ? [{
                    name: 'contractBasisAccount',
                    componentName: 'AccountSelector',
                    labelKey: 'Prebooking.pricingAccount',
                    disabled: true,
                }] as NestedField<keyof AccountLaneFormFields>[] : [],
                {
                    name: 'temperatureRange',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(LaneGroupTemperatureRange),
                    labelKey: 'AccountLane.temperatureRange',
                    required: true,
                    useDropdown: true,
                    disabled: disabled || tempRangeDisabled,
                },
                {
                    name: 'opportunity',
                    componentName: 'OpportunitiesSelector',
                    labelKey: 'AccountLane.opportunity',
                    includeFilters: opportunityFilters,
                    disabled,
                },
                {
                    name: 'leaseType',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(LeaseType),
                    labelKey: 'AccountLane.leaseType',
                    required: true,
                    showInColumns: true,
                    disabled,
                },
                ...exists ? [{
                    name: 'aQuoteId',
                    componentName: 'SkyNetLink',
                    link: quoteInLink,
                    text: quoteInNumber,
                    type: TypeLink.EXTERNAL,
                    size: SizeLink.S,
                    labelKey: 'AccountLaneForm.quotedIn',
                    disabled,
                }] as NestedField<keyof AccountLaneFormFields>[] : [],
            ],
        },
        {
            id: 'column2',
            fields: [
                {
                    name: 'laneStatus',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(LaneStatus),
                    labelKey: 'LaneQuoteRequest.selectedLaneStatus',
                    disabled: true,
                },
                {
                    name: 'expPalletsYear',
                    required: true,
                    componentName: 'InputSingleline',
                    labelKey: 'LaneBasicView.expPalletsYear',
                    disabled,
                },
                {
                    name: 'originAirport',
                    componentName: 'LocationObjectSelector',
                    filterLocationType: LocationType.AIRPORT,
                    labelKey: 'AccountLaneForm.originAirport',
                    required: true,
                    disabled,
                },
                {
                    name: 'destinationAirport',
                    componentName: 'LocationObjectSelector',
                    filterLocationType: LocationType.AIRPORT,
                    labelKey: 'AccountLaneForm.destinationAirport',
                    required: true,
                    disabled,
                },
                {
                    name: 'laneLead',
                    componentName: 'UserObjectSelector',
                    labelKey: 'AccountLane.laneLead',
                    required: true,
                    disabled: disabled || !laneLeadPermission,
                },
                {
                    name: 'laneSupporter',
                    componentName: 'UserObjectSelector',
                    labelKey: 'AccountLane.laneSupporter',
                    disabled: disabled || !laneLeadPermission,
                },
            ],
        },
        {
            id: 'column3',
            fields: [
                {
                    name: 'customerSite',
                    componentName: 'LocationObjectSelector',
                    labelKey: 'LaneUpdateNewDto.customerSite',
                    disabled,
                },
                {
                    name: 'billingCompany',
                    componentName: 'BillingCompanyObjectSelector',
                    labelKey: 'LaneUpdateNewDto.billingCompany',
                    required: requiredBillingCompany,
                    disabled,
                },
                {
                    name: 'customerExecution',
                    componentName: 'LocationObjectSelector',
                    labelKey: 'LaneQuoteRequest.selectedCustomerExecution',
                    filterLocationType: LocationType.ADDRESS,
                    filterAddressCategory: [
                        AddressCategory.CUSTOMER,
                        AddressCategory.CUSTOMER_QUOTATION,
                    ],
                    disabled,
                },
                {
                    name: 'customerExecutionProvisional',
                    componentName: 'InputSingleline',
                    labelKey: 'AccountLane.customerExecutionProvisional',
                    disabled,
                },
                {
                    name: 'customerExecutionContacts',
                    componentName: 'MultipleContactObjectSelector',
                    labelKey: 'AccountLane.customerExecutionContacts',
                    disabled,
                },
            ],
        },
        {
            id: 'column4',
            fields: [
                {
                    name: 'consigneeProvisional',
                    componentName: 'InputSingleline',
                    labelKey: 'AccountLane.consigneeProvisional',
                    disabled,
                },
                {
                    name: 'laneComment',
                    componentName: 'LongText',
                    labelKey: 'AccountLane.laneComment',
                    disabled,
                },
                {
                    name: 'createdOn',
                    componentName: 'DateTimeSelect',
                    disabled: true,
                    labelKey: 'Accounts.createdOn',
                },
                {
                    name: 'createdBy',
                    componentName: 'InputSingleline',
                    disabled: true,
                    labelKey: 'Accounts.createdBy',
                },
            ],
        },
    ]

    const productTypesFields: FieldInCard<keyof AccountLaneFormFields>[] = [{
        id: 'column1',
        fields: [{
            name: 'requestedProductTypes',
            componentName: 'EnumChipSelector',
            allowedValues: getAllowedValues(ProductType),
            labelKey: 'AccountLane.requestedProductTypes',
            disabled: true,
        }],
    }]

    if (showAwardedProductTypes) {
        productTypesFields.push({
            id: 'column2',
            fields: [{
                name: 'awardedProductTypes',
                componentName: 'EnumChipSelector',
                allowedValues: getAllowedValues(ProductType),
                labelKey: 'AccountLane.awardedProductTypes',
                disabled: true,
            }],
        })
    }

    const handoverFn = isAlternativeFlow ? getHandoverAlternateFields : getHandoverFields
    const collectionFn = isAlternativeFlow ? getCollectionAlternateFields : getCollectionFields

    const defaultSections = [
        {
            id: 'general',
            title: 'General',
            className: 'general',
            fields: generalFields,
        },
        ...(showHandoverCollectionSection ? [
            {
                id: 'handover',
                title: 'Handover',
                className: 'handover',
                fields: handoverFn({
                    getAllowedValues,
                    disabled,
                    logisticDetails,
                    showFTL,
                    showLTL,
                    handoverPointCountry,
                    isDeliveryRoad,
                }),
            },
            {
                id: 'collection',
                title: 'Collection',
                className: 'collection',
                fields: collectionFn({
                    disabled,
                    getAllowedValues,
                    isCollection,
                    collectionDropoffPointCountry,
                    logisticDetails,
                }),
            },
        ] : []),
    ]

    return showProductTypes ? [
        ...defaultSections,
        {
            id: 'productTypes',
            title: 'Product Types',
            className: 'productTypes',
            fields: productTypesFields,
        },
    ] : defaultSections
}

export default getFields
